import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';

const MONTHS = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'
];

export default function CallCenterStatsModal({ onClose, isDarkMode, currentUserId }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [stats, setStats] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { buttonDescription, companies, loading }

  const openDetail = async (buttonDescription) => {
    setDetailModal({ buttonDescription, companies: [], loading: true });
    try {
      const res = await fetch(
        `${API_URL}/callcenter/stats/batch-detail?userId=${currentUserId}&buttonDescription=${encodeURIComponent(buttonDescription)}&month=${selectedMonth}&year=${selectedYear}`
      );
      const data = res.ok ? await res.json() : [];
      setDetailModal({ buttonDescription, companies: data, loading: false });
    } catch {
      setDetailModal({ buttonDescription, companies: [], loading: false });
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  useEffect(() => {
    if (config) fetchStats();
  }, [selectedMonth, selectedYear, config]);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/callcenter/config?userId=${currentUserId}`);
      if (res.ok) setConfig(await res.json());
    } catch (e) {
      setConfig({ buttons: [] });
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/callcenter/stats/summary?month=${selectedMonth}&year=${selectedYear}&userId=${currentUserId}&callcenterUserId=${currentUserId}`
      );
      if (res.ok) setStats(await res.json());
    } catch (e) {
      setStats([]);
    } finally {
      setLoading(false);
    }
  };

  // Všechna data jsou už pro aktuálního uživatele (filtrováno na backendu)
  const myStats = stats;

  // Seskupit dle tlačítek (stejná logika jako CallCenterAdmin)
  const grouped = myStats.reduce((acc, stat) => {
    if (!acc[stat.userId]) {
      acc[stat.userId] = { userId: stat.userId, userName: stat.userName, buttons: {} };
    }
    if (!acc[stat.userId].buttons[stat.buttonDescription]) {
      acc[stat.userId].buttons[stat.buttonDescription] = { count: 0, totalValue: 0 };
    }
    acc[stat.userId].buttons[stat.buttonDescription].count += stat.count;
    acc[stat.userId].buttons[stat.buttonDescription].totalValue += (stat.totalValue || 0);
    return acc;
  }, {});

  const userStat = Object.values(grouped)[0] || null;
  const activeButtons = config?.buttons?.filter(b => b.isActive) || [];
  const total = userStat ? Object.values(userStat.buttons).reduce((s, b) => s + b.count, 0) : 0;
  const totalValue = userStat ? Object.values(userStat.buttons).reduce((s, b) => s + (b.totalValue || 0), 0) : 0;

  return (
    <>
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className={`rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h2 className="text-lg font-bold">📊 Statistika leadů</h2>
          <button onClick={onClose} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
        </div>

        {/* Filtry */}
        <div className={`px-6 py-3 flex gap-3 items-center border-b ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-100 bg-gray-50'}`}>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(parseInt(e.target.value))}
            className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
          >
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={e => setSelectedYear(parseInt(e.target.value))}
            className={`px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-300'}`}
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button
            onClick={fetchStats}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50"
          >
            {loading ? 'Načítám...' : '🔄 Obnovit'}
          </button>
        </div>

        {/* Obsah */}
        <div className="overflow-auto flex-1 p-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Načítám data...</div>
          ) : activeButtons.length === 0 ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Žádné aktivní tlačítka nejsou nakonfigurována.
            </div>
          ) : !userStat || total === 0 ? (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Žádné statistiky pro {MONTHS[selectedMonth - 1]} {selectedYear}.
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                    {activeButtons.map(btn => (
                      <th key={btn._id} className={`px-4 py-3 text-center font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                        {btn.description}
                      </th>
                    ))}
                    <th className={`px-4 py-3 text-center font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Celkem</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={isDarkMode ? 'bg-gray-800' : 'bg-white'}>
                    {activeButtons.map(btn => {
                      const count = userStat.buttons[btn.description]?.count || 0;
                      const val = userStat.buttons[btn.description]?.totalValue || 0;
                      return (
                        <td key={btn._id} className="px-4 py-4 text-center">
                          {count > 0 ? (
                            <div className="flex flex-col items-center gap-1">
                              <button
                                onClick={() => openDetail(btn.description)}
                                className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-full font-semibold text-base transition cursor-pointer"
                                title="Zobrazit seznam firem"
                              >
                                {count}
                              </button>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {val.toLocaleString('cs-CZ')} Kč
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-4 text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-block bg-green-600 text-white px-4 py-1 rounded-full font-bold text-base">
                          {total}
                        </span>
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400">
                          {totalValue.toLocaleString('cs-CZ')} Kč
                        </span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Rozpad leadů podle obchodníka */}
            {(() => {
              const perObchodnik = {};
              myStats.forEach(s => {
                (s.assignedToList || []).forEach(email => {
                  if (!email) return;
                  perObchodnik[email] = (perObchodnik[email] || 0) + 1;
                });
              });
              const rows = Object.entries(perObchodnik).sort((a, b) => b[1] - a[1]);
              if (rows.length === 0) return null;
              return (
                <div className={`mt-6 rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                  <div className={`px-4 py-2 font-semibold text-sm ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                    👤 Komu šly leady
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                        <th className="px-4 py-2 text-left font-medium">Obchodník (email)</th>
                        <th className="px-4 py-2 text-center font-medium">Počet leadů</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(([email, count], i) => (
                        <tr key={email} className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'} ${i % 2 !== 0 ? isDarkMode ? 'bg-gray-750' : 'bg-gray-50' : ''}`}>
                          <td className={`px-4 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{email}</td>
                          <td className="px-4 py-2 text-center">
                            <span className={`font-semibold px-2 py-0.5 rounded-full text-xs ${isDarkMode ? 'bg-blue-700 text-blue-200' : 'bg-blue-100 text-blue-700'}`}>
                              {count}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
            </>
          )}
        </div>
      </div>
    </div>
    {detailModal && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 p-4" onClick={() => setDetailModal(null)}>
        <div
          className={`rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h3 className="font-bold text-base">📋 {detailModal.buttonDescription}</h3>
            <button onClick={() => setDetailModal(null)} className={`text-xl ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>✕</button>
          </div>
          <div className="overflow-auto flex-1 p-4">
            {detailModal.loading ? (
              <div className="text-center py-10 text-gray-500">Načítám...</div>
            ) : detailModal.companies.length === 0 ? (
              <div className={`text-center py-10 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné firmy.</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-200'}`}>
                    <th className="px-3 py-2 text-left font-semibold">Název firmy</th>
                    <th className="px-3 py-2 text-left font-semibold">IČO</th>
                    <th className="px-3 py-2 text-left font-semibold">Vlastník</th>
                    <th className="px-3 py-2 text-left font-semibold">Stav</th>
                    <th className="px-3 py-2 text-left font-semibold">FC</th>
                  </tr>
                </thead>
                <tbody>
                  {detailModal.companies.map((c, i) => (
                    <tr key={i} className={`border-b ${isDarkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-gray-100 hover:bg-gray-50'}`}>
                      <td className="px-3 py-2 font-medium">{c.companyName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{c.ico || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.ownerName || '—'}</td>
                      <td className={`px-3 py-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{c.status || '—'}</td>
                      <td className="px-3 py-2">
                        {c.forecast ? (
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.forecast === '100' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {c.forecast}
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    )}
  </>
  );
}
